import { inject, observer } from "mobx-react";
import React, { useState, useCallback } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "randmarcomps";
import { Product } from "../../stores/product/Product";
import { RootStore } from "../../stores/rootStore";
import InlineSpinner from "../util/InlineSpinner";
import RichTextEditor from "../util/textEditor/RichTextEditor";

interface IProps {
  product: Product;
  applicationId: string;
  store?: RootStore;
}

const EditShopifyListing: React.FC<IProps> = ({ product, applicationId, store }) => {
  const [title, setTitle] = useState(product.title);
  const [body, setBody] = useState(product.bodyHTML);
  const [map, setMap] = useState<string>(product.map?.toString() ?? "");
  const [msrp, setMsrp] = useState<string>(product.msrp?.toString() ?? "");
  const [loading, setLoading] = useState(false);
  const [voiceoverCaption, setVoiceoverCaption] = useState(product.voiceoverCaption);
  const [sceneName, setSceneName] = useState(product.sceneName || "Randmar");

  const { mediaStore: { scenes } } = store!;

  const saveInformation = useCallback(async (propName: string) => {
    const { productStore, snackStore } = store!;
    let success = true;

    setLoading(true);

    if (propName === "Title") {
      const res = await productStore.setProductProperty(applicationId, product.randmarSKU!, "Title", title!)
      if (!res) success = false;
    }
    if (propName === "BodyHTML") {
      const res = await productStore.setProductBodyProperty(applicationId, product.randmarSKU!, "BodyHTML", body!)
      if (!res) success = false;
    }
    if (propName === "MAP") {
      const res = await productStore.setProductProperty(applicationId, product.randmarSKU!, "MAP", map)
      if (!res) success = false;
    }
    if (propName === "MSRP") {
      const res = await productStore.setProductProperty(applicationId, product.randmarSKU!, "MSRP", msrp)
      if (!res) success = false;
    }
    if (propName === "VoiceoverCaption") {
      const res = await productStore.setProductProperty(applicationId, product.randmarSKU!, "VoiceoverCaption", voiceoverCaption!)
      if (!res) success = false;
    }
    if (propName === "SceneName") {
      const res = await productStore.setProductProperty(applicationId, product.randmarSKU!, "SceneName", sceneName!)
      if (!res) success = false;
    }

    if (success) snackStore.showSuccess("Information updated");
    else snackStore.showError("Could not update information");

    productStore.getProduct(applicationId, product.randmarSKU!);

    setLoading(false);
  }, [applicationId, product.randmarSKU, store, title, body, map, msrp, voiceoverCaption, sceneName]);

  return (
    <div className="space-y-6">
      <Alert>
        <AlertTitle>Why Shopify?</AlertTitle>
        <AlertDescription>
          Filling out these fields will enable resellers with Shopify websites to add this product from the Randmar Reseller Portal.
          Keeping the online title, MAP, description, UPC, and product image up to date will push updates to reseller sites quickly.
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="shopify-title">Title</Label>
          <div className="flex gap-2">
            <Input id="shopify-title" value={title} onChange={(e) => setTitle(e.target.value)} disabled={loading} />
            <Button
              type="button"
              onClick={() => saveInformation("Title")}
              disabled={loading || product?.title === title}
              className="shrink-0"
            >
              {loading ? <InlineSpinner className="mr-2" decorative /> : null}
              Update
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="shopify-map">MAP</Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                $
              </span>
              <Input
                id="shopify-map"
                type="number"
                inputMode="decimal"
                value={map}
                onChange={(e) => setMap(e.target.value)}
                className="pl-7"
                disabled={loading}
              />
            </div>
            <Button
              type="button"
              onClick={() => saveInformation("MAP")}
              disabled={loading || product?.map?.toString() === map}
              className="shrink-0"
            >
              {loading ? <InlineSpinner className="mr-2" decorative /> : null}
              Update
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="shopify-msrp">MSRP</Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                $
              </span>
              <Input
                id="shopify-msrp"
                type="number"
                inputMode="decimal"
                value={msrp}
                onChange={(e) => setMsrp(e.target.value)}
                className="pl-7"
                disabled={loading}
              />
            </div>
            <Button
              type="button"
              onClick={() => saveInformation("MSRP")}
              disabled={loading || product?.msrp?.toString() === msrp}
              className="shrink-0"
            >
              {loading ? <InlineSpinner className="mr-2" decorative /> : null}
              Update
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-[1fr_260px_auto] md:items-end">
        <div className="space-y-2">
          <Label htmlFor="shopify-voiceover">Voiceover caption</Label>
          <Textarea
            id="shopify-voiceover"
            value={voiceoverCaption}
            onChange={(e) => setVoiceoverCaption(e.target.value)}
            rows={3}
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Label>Scene</Label>
          <Select value={sceneName} onValueChange={setSceneName} disabled={loading}>
            <SelectTrigger className="h-11 rounded-xl">
              <SelectValue placeholder="Select a scene" />
            </SelectTrigger>
            <SelectContent>
              {(scenes ?? [])
                .filter((row): row is { name: string } => Boolean(row?.name))
                .map((row) => (
                  <SelectItem key={row.name} value={row.name}>
                    {row.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => saveInformation("VoiceoverCaption")}
            disabled={loading || product?.voiceoverCaption === voiceoverCaption}
            variant="outline"
          >
            {loading ? <InlineSpinner className="mr-2" decorative /> : null}
            Update caption
          </Button>
          <Button
            type="button"
            onClick={() => saveInformation("SceneName")}
            disabled={loading || product?.sceneName === sceneName}
            variant="outline"
          >
            {loading ? <InlineSpinner className="mr-2" decorative /> : null}
            Update scene
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
      <RichTextEditor
        initialContent={product?.bodyHTML || ""}
        setContent={(newValue: string) => setBody(newValue)}
      />
      {/* <RichTextEditor setValue={(newValue: string) => this.setState({ body: newValue })} initialValue={product?.bodyHTML || ""} /> */}
      </div>
      <div className="flex justify-end">
        <Button
          type="button"
          variant="default"
          disabled={product?.bodyHTML === body}
          onClick={() => saveInformation("BodyHTML")}
        >
          {loading ? <InlineSpinner className="mr-2" decorative /> : null}
          Save description
        </Button>
      </div>
    </div>
  );
};

export default inject("store")(observer(EditShopifyListing));
